import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, FormMessage, Icon, ModalCard } from '~/components';
import { useApi } from '~/contexts';
import { useForm } from '~/hooks';
import { colors, weights } from '~/styles';

const Name = styled.p`
  margin-bottom: 1rem;

  > span {
    font-weight: ${weights.bold};
  }
`;

const Label = styled.label`
  display: block;
  font-weight: ${weights.bold};
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  margin-top: 1rem;

  &:first-of-type {
    margin-top: 0;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 6rem;
  padding: 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
  border: solid 1px #ccc;
  border-radius: 0.25rem;
  resize: vertical;
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${colors.grey75};
`;

export default function EditNotes({ workspace, onChange, onClose }) {
  const [{ isSubmitting, message, status }, form] = useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [generalNotes, setGeneralNotes] = useState('');
  const [specialContractTerms, setSpecialContractTerms] = useState('');
  const api = useApi();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.admin.workspaces.getGeneral(workspace.id);
        setGeneralNotes(data.adminNotes?.generalNotes || '');
        setSpecialContractTerms(data.adminNotes?.specialContractTerms || '');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [api, workspace.id]);

  const handleSubmit = async (scope) => {
    form.submit(scope);

    try {
      await api.admin.workspaces.updateNotes(workspace.id, {
        generalNotes: generalNotes || null,
        specialContractTerms: specialContractTerms || null,
      });

      onChange();
      onClose();
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  };

  return (
    <ModalCard title="Edit Notes" onClose={onClose}>
      <ModalCard.Body>
        {status && <FormMessage.Error style={{ marginBottom: '1rem' }}>{message}</FormMessage.Error>}
        <Name>
          <span>{workspace.name}</span> ({workspace.key})
        </Name>
        {isLoading ? (
          <Loading>
            <Icon icon="spinner" spin={true} />
          </Loading>
        ) : (
          <>
            <Label>General Notes</Label>
            <TextArea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} rows={4} />
            <Label>Special Contract Terms</Label>
            <TextArea value={specialContractTerms} onChange={(e) => setSpecialContractTerms(e.target.value)} rows={4} />
          </>
        )}
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <Button isLoading={isSubmitting} onClick={handleSubmit}>
            Save
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}
